import React, { useState, useEffect } from 'react';
import { CreditCard, Calendar, AlertTriangle, CheckCircle, XCircle, RefreshCw, Crown } from 'lucide-react';
import { subscriptionAPI, Subscription, SubscriptionStatus } from '../../services/subscriptionAPI';
import CardManagement from './CardManagement';

interface SubscriptionManagementProps {
  siteId: string;
  isSuperAdmin?: boolean;
}

const SubscriptionManagement: React.FC<SubscriptionManagementProps> = ({ 
  siteId, 
  isSuperAdmin = false 
}) => {
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [activeTab, setActiveTab] = useState<'overview' | 'payment' | 'billing'>('overview');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    loadSubscriptionData();
  }, [siteId]);

  const loadSubscriptionData = async () => {
    try {
      setLoading(true);
      setError('');
      
      // First try to get subscription status
      const statusResponse = await subscriptionAPI.getSubscriptionStatus(siteId);
      setSubscriptionStatus(statusResponse.data);
      
      // If subscription exists, get full details
      if (statusResponse.data.hasSubscription) {
        const subscriptionResponse = await subscriptionAPI.getSubscription(siteId);
        setSubscription(subscriptionResponse.data);
      } else {
        setSubscription(null);
      }
    } catch (error: any) {
      console.error('Subscription load error:', error);
      if (error.response?.status === 403) {
        setError('Access denied. You do not have permission to manage billing for this site.');
      } else if (error.response?.status === 404) {
        // No subscription exists - this is normal
        setSubscription(null);
        const statusResponse = await subscriptionAPI.getSubscriptionStatus(siteId);
        setSubscriptionStatus(statusResponse.data);
      } else {
        setError(error.response?.data?.error || 'Failed to load subscription data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel this subscription? This will take effect at the end of the current billing period.')) {
      return;
    }

    try {
      setProcessing(true);
      await subscriptionAPI.cancelSubscription(siteId, true);
      setSuccess('Subscription canceled successfully');
      loadSubscriptionData();
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to cancel subscription');
    } finally {
      setProcessing(false);
    }
  };

  const handleCreateSubscription = async () => {
    // First check if there are any payment methods
    try {
      const paymentMethodsResponse = await subscriptionAPI.getPaymentMethods(siteId);
      const paymentMethods = paymentMethodsResponse.data.paymentMethods || [];
      
      if (paymentMethods.length === 0) {
        // No payment methods - redirect to payment tab and show message
        setActiveTab('payment');
        setError('Please add a payment method first, then create a subscription.');
        return;
      }
      
      // Has payment methods - create subscription with default one
      const defaultPaymentMethodId = paymentMethodsResponse.data.defaultPaymentMethodId || paymentMethods[0].stripePaymentMethodId;
      
      setProcessing(true);
      await subscriptionAPI.createSubscription({
        siteId,
        paymentMethodId: defaultPaymentMethodId
      });
      
      setSuccess('Subscription created successfully!');
      loadSubscriptionData();
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to create subscription');
    } finally {
      setProcessing(false);
    }
  };

  const handleReactivateSubscription = async () => {
    if (!window.confirm('Are you sure you want to reactivate this subscription?')) {
      return;
    }

    try {
      setProcessing(true);
      // This would be a custom endpoint to reactivate
      setSuccess('Subscription reactivated successfully');
      loadSubscriptionData();
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to reactivate subscription');
    } finally {
      setProcessing(false);
    }
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'trialing':
        return 'bg-blue-100 text-blue-800';
      case 'past_due':
        return 'bg-yellow-100 text-yellow-800';
      case 'canceled':
        return 'bg-red-100 text-red-800';
      case 'unpaid':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status?: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="w-4 h-4" />;
      case 'trialing':
        return <Crown className="w-4 h-4" />;
      case 'past_due':
        return <AlertTriangle className="w-4 h-4" />;
      case 'canceled':
        return <XCircle className="w-4 h-4" />;
      case 'unpaid':
        return <XCircle className="w-4 h-4" />;
      default:
        return <AlertTriangle className="w-4 h-4" />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Subscription Details</h2>
          <p className="text-gray-600">Manage your subscription plan, payment methods, and billing settings</p>
        </div>
        <button
          onClick={loadSubscriptionData}
          className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
        >
          <RefreshCw className="w-5 h-5" />
        </button>
      </div>

      {/* Alerts */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            <p className="text-red-800">{error}</p>
          </div>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <p className="text-green-800">{success}</p>
          </div>
        </div>
      )}

      {/* No Subscription State */}
      {!subscriptionStatus?.hasSubscription ? (
        <div className="bg-white rounded-lg shadow-sm border p-8 text-center">
          <CreditCard className="w-12 h-12 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Active Subscription</h3>
          <p className="text-gray-600 mb-4">
            Set up a subscription to enable premium features and automated billing
          </p>
          <button 
            onClick={handleCreateSubscription}
            disabled={processing}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {processing ? 'Creating...' : 'Create Subscription'}
          </button>
        </div>
      ) : (
        <>
          {/* Tabs */}
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('overview')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'overview'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Overview
              </button>
              <button
                onClick={() => setActiveTab('payment')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'payment'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Payment Methods
              </button>
              <button
                onClick={() => setActiveTab('billing')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'billing'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Billing History
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Subscription Status */}
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Subscription Status</h3>
                
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <span className={`inline-flex items-center px-3 py-1 text-sm rounded-full ${getStatusColor(subscriptionStatus.status)}`}>
                      {getStatusIcon(subscriptionStatus.status)}
                      <span className="ml-1 capitalize">{subscriptionStatus.status?.replace('_', ' ')}</span>
                    </span>
                    {subscriptionStatus.cancelAtPeriodEnd && (
                      <span className="text-sm text-gray-600">
                        Cancels on {formatDate(subscriptionStatus.nextBillingDate || '')}
                      </span>
                    )}
                  </div>
                  
                  {isSuperAdmin && (
                    <div className="flex items-center space-x-2">
                      {subscriptionStatus.cancelAtPeriodEnd ? (
                        <button
                          onClick={handleReactivateSubscription}
                          disabled={processing}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                        >
                          Reactivate
                        </button>
                      ) : (
                        <button
                          onClick={handleCancelSubscription}
                          disabled={processing}
                          className="px-4 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 disabled:opacity-50"
                        >
                          Cancel Subscription
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Billing Period */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">Current Period</p>
                    <p className="font-medium">
                      {formatDate(subscription?.currentPeriodStart || '')} - {formatDate(subscription?.currentPeriodEnd || '')}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-600">Next Billing Date</p>
                    <p className="font-medium">
                      {formatDate(subscriptionStatus.nextBillingDate || '')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Pricing Details */}
              {subscription?.pricing && (
                <div className="bg-white rounded-lg shadow-sm border p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Pricing Details</h3>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Base Price</span>
                      <span className="font-medium">
                        {subscription.pricing.basePrice.currency} {subscription.pricing.basePrice.amount.toFixed(2)}
                      </span>
                    </div>
                    
                    {subscription.items.map((item, index) => (
                      <div key={index} className="flex justify-between text-sm">
                        <span className="text-gray-600">{item.description}</span>
                        <span className="font-medium">
                          {item.currency} {item.amount.toFixed(2)}
                        </span>
                      </div>
                    ))}
                    
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Tax ({subscription.pricing.tax.rate}%)</span>
                      <span className="font-medium">
                        {subscription.pricing.basePrice.currency} {subscription.pricing.tax.amount.toFixed(2)}
                      </span>
                    </div>
                    
                    <div className="border-t pt-3">
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-900">Total</span>
                        <span className="font-medium text-lg text-primary-600">
                          {subscription.pricing.basePrice.currency} {subscription.pricing.totalAmount.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Default Payment Method */}
              {subscription?.defaultPaymentMethodId && (
                <div className="bg-white rounded-lg shadow-sm border p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Default Payment Method</h3>
                  
                  {(() => {
                    const defaultMethod = subscription.paymentMethods.find(
                      method => method.stripePaymentMethodId === subscription.defaultPaymentMethodId
                    );
                    return defaultMethod ? (
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-gray-100 rounded-lg">
                          <CreditCard className="w-5 h-5 text-gray-600" />
                        </div>
                        <div>
                          <p className="font-medium capitalize">
                            {defaultMethod.brand} ending in {defaultMethod.last4}
                          </p>
                          <p className="text-sm text-gray-600">
                            Expires {defaultMethod.expMonth}/{defaultMethod.expYear}
                          </p>
                        </div>
                      </div>
                    ) : null;
                  })()}
                </div>
              )}
            </div>
          )}

          {activeTab === 'payment' && (
            <CardManagement siteId={siteId} />
          )}

          {activeTab === 'billing' && (
            <div className="bg-white rounded-lg shadow-sm border p-8 text-center">
              <Calendar className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Billing History</h3>
              <p className="text-gray-600">
                Invoice history will be displayed here once billing is active
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SubscriptionManagement;
